<template>
  <p class="online-pill" :class="{ 'online-pill--pending': count === null }" :aria-hidden="count === null ? 'true' : undefined">
    <span class="online-pill__dot" aria-hidden="true"></span>
    <span class="online-pill__count cb-num">{{ countText }}</span>
    <span>{{ noun }} в сети</span>
  </p>
</template>

<script setup>
/**
 * OnlinePill — плашка «● N игроков в сети» на главном экране.
 *
 * Опрашивает GET /api/game/players-online раз в 15 c, пока экран открыт и вкладка видна; при возврате
 * на вкладку и после переподключения WebSocket обновляет сразу. До первого ответа прозрачна (место
 * зарезервировано, чтобы hero не прыгал), при ошибке показывает последнее известное значение.
 */
import { computed, onMounted, onUnmounted, ref } from "vue";
import { getPlayersOnline } from "@/services/api";
import realtime from "@/services/realtime";
import { formatNumber, pluralRu } from "@/utils/format";

const POLL_MS = 15000;

const count = ref(null);

const countText = computed(() => (count.value === null ? "—" : formatNumber(count.value)));
const noun = computed(() => pluralRu(count.value || 0, "игрок", "игрока", "игроков"));

let timer = null;
let loading = false;
let disposed = false;
let offReconnected = null;

function stopPolling() {
  clearTimeout(timer);
  timer = null;
}

async function refresh() {
  stopPolling();
  if (disposed || loading) return;
  loading = true;
  try {
    const data = await getPlayersOnline();
    const value = Number(data && data.playersOnline);
    if (!disposed && Number.isFinite(value) && value >= 0) count.value = Math.floor(value);
  } catch (error) {
    // оставляем прежнее значение; 401 обработает api.js
  } finally {
    loading = false;
  }
  // Скрытая вкладка: опрос на паузе, продолжится по visibilitychange.
  if (!disposed && !document.hidden) timer = setTimeout(refresh, POLL_MS);
}

function onVisibilityChange() {
  if (document.hidden) stopPolling();
  else refresh();
}

onMounted(() => {
  refresh();
  document.addEventListener("visibilitychange", onVisibilityChange);
  offReconnected = realtime.on("reconnected", refresh);
});

onUnmounted(() => {
  disposed = true;
  stopPolling();
  document.removeEventListener("visibilitychange", onVisibilityChange);
  if (offReconnected) offReconnected();
});
</script>

<style scoped>
.online-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  margin-top: 6px;
  padding: 0 13px 0 11px;
  border-radius: var(--cb-radius-pill);
  background: rgba(11, 14, 10, 0.6);
  border: 1px solid rgba(221, 245, 90, 0.22);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1;
  color: #C9CEC3;
  white-space: nowrap;
  transition: opacity var(--cb-dur-slow) var(--cb-ease);
}

.online-pill--pending {
  opacity: 0;
}

.online-pill__dot {
  position: relative;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--cb-lime);
  box-shadow: 0 0 8px rgba(221, 245, 90, 0.8);
}

/* Расходящийся «пинг» вокруг точки. */
.online-pill__dot::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--cb-lime);
  opacity: 0;
  animation: online-pill-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.online-pill__count {
  font-weight: 700;
  color: var(--cb-lime);
}

@keyframes online-pill-ping {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  80%,
  100% {
    transform: scale(2.6);
    opacity: 0;
  }
}
</style>
